import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bounce, ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table
} from "reactstrap";
import api from "../../services/api";

const RefundsList = () => {
  const navigate = useNavigate();
  document.title = "Technical Hub | Refunds List";
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const backendFileUrl = process.env.REACT_APP_BACKEND_IMAGE_URL;

  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchRollNo, setSearchRollNo] = useState("");
  const [searching, setSearching] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    let authUser = JSON.parse(localStorage.getItem('authUser'));
    setUser(authUser)
    console.log(authUser)
  }, [])

  // Static values as per requirement
  const REFUND_GIVEN_BY = user ? user.first_name : "Unknown";
  const REFUND_CASH_COUNTER = user ? user.college : "Unknown";

  // Fetch refunds by roll number
  const fetchRefundsByRollNo = async () => {
    if (!searchRollNo.trim()) {
      toast.error("Please enter a roll number");
      return;
    }

    try {
      setSearching(true);
      setHasSearched(true);
      const response = await api.post(
        `${backendUrl}get-pending-refunds-by-rollno`,
        { roll_no: searchRollNo.trim() }
      );

      if (response.data.success && response.data.data.length > 0) {
        setRefunds(response.data.data);
        console.log(response.data.data);

        toast.success(`Found ${response.data.data.length} pending refund(s)`);
      } else {
        setRefunds([]);
        toast.info("No pending refunds found for this roll number");
      }
    } catch (error) {
      console.error("Error fetching refunds:", error);
      setRefunds([]);
      if (error.response) {
        toast.error(error.response.data.message || "Failed to fetch refunds");
      } else {
        toast.error("Failed to fetch refunds. Please try again.");
      }
    } finally {
      setSearching(false);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    fetchRefundsByRollNo();
  };

  // Open refund modal
  const openRefundModal = (refund) => {
    setSelectedRefund(refund);
    setModalOpen(true);
  };

  // Close refund modal
  const closeRefundModal = () => {
    setModalOpen(false);
    setSelectedRefund(null);
  };

  // Process refund - Updated API call
  const processRefund = async () => {
    try {
      setSubmitting(true);

      // Prepare data for the new API
      const refundData = {
        _id: selectedRefund._id,
        payment_id: selectedRefund.payment_id,
        refund_given_by: REFUND_GIVEN_BY,
        refund_cashcounter: REFUND_CASH_COUNTER,
        refund_given_date: new Date().toISOString()
      };

      console.log("Sending refund data:", refundData);

      const response = await api.post(
        `${backendUrl}update-refund-status`,
        refundData
      );

      if (response.data.success) {
        toast.success("Refund processed successfully!");
        closeRefundModal();

        // Navigate to receipt page with refund data
        const receiptData = {
          ...selectedRefund,
          refund_given_by: REFUND_GIVEN_BY,
          refund_cashcounter: REFUND_CASH_COUNTER,
          refund_given_date: new Date().toISOString(),
          processed_refund_id: response.data.refundId || selectedRefund._id
        };

        // Navigate to receipt page with data
        navigate("/refund-receipt", { state: { receiptData } });
      }
    } catch (error) {
      console.error("Error processing refund:", error);
      if (error.response) {
        toast.error(error.response.data.message || "Failed to process refund");
      } else {
        toast.error("Failed to process refund. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Download file
  const downloadFile = (filePath) => {
    if (filePath) {
      const fullUrl = `${backendFileUrl}${filePath}`;
      window.open(fullUrl, '_blank');
    } else {
      toast.info("No file attached");
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN');
  };

  // Format date with time
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN');
  };

  return (
    <React.Fragment>
      <Row>
        <ToastContainer
          position="top-right"
          autoClose={500}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          transition={Bounce}
        />

        <Col xl="12">
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="card-title mb-0">Approve Refunds</h4>
                {/* {user && (user.role==="developer") && (
                <Button color="primary" onClick={() => navigate("/add-refund")}>
                  Add New Refund
                </Button>
                )} */}
              </div>

              {/* Search Section */}
              <Card className="bg-light mb-4 border-0">
                <CardBody className="text-center py-4">
                  <h6 className="mb-3 text-muted">Search Refunds</h6>
                  <form onSubmit={handleSearch} className="d-inline-block">
                    <div className="d-flex flex-column flex-md-row gap-3 align-items-center">
                      <Input
                        type="text"
                        placeholder="Roll Number"
                        value={searchRollNo}
                        onChange={(e) => setSearchRollNo(e.target.value)}
                        disabled={searching}
                        className="text-center"
                        style={{ minWidth: '200px' }}
                      />
                      <Button
                        color="primary"
                        type="submit"
                        disabled={searching || !searchRollNo.trim()}
                        style={{ minWidth: '140px' }}
                      >
                        {searching ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Searching
                          </>
                        ) : (
                          "Search"
                        )}
                      </Button>
                    </div>
                  </form>
                </CardBody>
              </Card>

              {/* Results Section */}
              {refunds.length > 0 && (
                <Card className="mt-4">
                  <CardBody>
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Roll No</th>
                            <th>Student Name</th>
                            <th>Course</th>
                            <th>Technology</th>
                            <th>Term</th>
                            <th>Payment ID</th>
                            <th>Amount</th>
                            <th>Payment Date</th>
                            <th>Document</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {refunds.map((refund, index) => (
                            <tr key={refund._id}>
                              <td>{index + 1}</td>
                              <td>
                                <strong>{refund.roll_no}</strong>
                              </td>
                              <td>{refund.name}</td>
                              <td>{refund.course_name || 'N/A'}</td>
                              <td>{refund.technology_name || 'N/A'}</td>
                              <td>{refund.term}</td>
                              <td>
                                <code>{refund.payment_id}</code>
                              </td>
                              <td>
                                <span className="fw-bold text-success">
                                  ₹{refund.refund_amount}
                                </span>
                              </td>
                              <td>{formatDate(refund.payment_date)}</td>
                              <td>
                                {refund.file ? (
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => downloadFile(refund.file)}
                                  >
                                    View Doc
                                  </Button>
                                ) : (
                                  <span className="text-muted">No File</span>
                                )}
                              </td>
                              <td>
                                <Button
                                  color="success"
                                  size="sm"
                                  onClick={() => openRefundModal(refund)}
                                >
                                  Process Refund
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              )}

              {/* No Results Message - Only show after search */}
              {hasSearched && !searching && refunds.length === 0 && (
                <div className="d-flex align-items-center justify-content-center text-muted py-4">
                  <i className="bx bx-search-alt me-2" style={{ fontSize: '0.8rem' }}></i>
                  <span style={{ fontSize: '0.85rem' }}>
                    No refunds found for <strong className="text-dark">{searchRollNo}</strong>
                  </span>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Refund Confirmation Modal */}
      <Modal isOpen={modalOpen} toggle={closeRefundModal} size="lg">
        <ModalHeader toggle={closeRefundModal}>
          <i className="bx bx-check-shield text-success me-2"></i>
          Confirm Refund Processing
        </ModalHeader>
        <ModalBody>
          {selectedRefund && (
            <div>
              {/* Receipt Style Display */}
              <div className="receipt-container mb-4">
                <div className="receipt-header text-center py-3 bg-primary text-white rounded-top">
                  <h5 className="mb-1 fw-bold">TECHNICAL HUB</h5>
                  <p className="mb-0 opacity-75">Refund Processing Receipt</p>
                </div>

                <div className="receipt-body bg-white p-3 border border-top-0 rounded-bottom">
                  {/* Student Information */}
                  <div className="row mb-3">
                    <div className="col-6">
                      <small className="text-muted d-block">Roll Number</small>
                      <strong>{selectedRefund.roll_no}</strong>
                    </div>
                    <div className="col-6">
                      <small className="text-muted d-block">Student Name</small>
                      <strong>{selectedRefund.name}</strong>
                    </div>
                  </div>

                  {/* Course Details */}
                  <div className="row mb-3">
                    <div className="col-4">
                      <small className="text-muted d-block">Course</small>
                      <strong>{selectedRefund.course_name || 'N/A'}</strong>
                    </div>
                    <div className="col-4">
                      <small className="text-muted d-block">Technology</small>
                      <strong>{selectedRefund.technology_name || 'N/A'}</strong>
                    </div>
                    <div className="col-4">
                      <small className="text-muted d-block">Term</small>
                      <strong>{selectedRefund.term || 'N/A'}</strong>
                    </div>
                  </div>

                  {/* Payment Information */}
                  <div className="row mb-3">
                    <div className="col-6">
                      <small className="text-muted d-block">Payment ID</small>
                      <code className="text-primary">{selectedRefund.payment_id}</code>
                    </div>
                    <div className="col-6">
                      <small className="text-muted d-block">Payment Date</small>
                      <strong>{formatDate(selectedRefund.payment_date)}</strong>
                    </div>
                  </div>

                  {/* Refund Details */}
                  <div className="row mb-3">
                    <div className="col-6">
                      <small className="text-muted d-block">Refund Amount</small>
                      <h5 className="text-success mb-0">₹{selectedRefund.refund_amount}</h5>
                    </div>
                    <div className="col-6">
                      <small className="text-muted d-block">Refund Reason</small>
                      <strong>{selectedRefund.refund_reason || 'N/A'}</strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* Confirmation Message */}
              <Alert color="warning" className="mb-0">
                <div className="d-flex align-items-center">
                  <i className="bx bx-info-circle fs-4 me-2"></i>
                  <div>
                    <strong>Confirmation Required</strong>
                    <p className="mb-0">Are you sure you want to process this refund? This action cannot be undone.</p>
                  </div>
                </div>
              </Alert>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeRefundModal} disabled={submitting}>
            <i className="bx bx-x me-1"></i>
            Cancel
          </Button>
          <Button color="success" onClick={processRefund} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Processing Refund...
              </>
            ) : (
              <>
                <i className="bx bx-check me-1"></i>
                Confirm Refund
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default RefundsList;
